/**
 * Error Message Mapper
 *
 * Maps HTTP status codes and error types to user-friendly messages.
 * Provides consistent error messaging across the OCPI settings feature.
 */

/**
 * Map HTTP status code to user-friendly error message
 */
export function getHttpErrorMessage(statusCode: number, context: 'ocpi-cpo' | 'kiosk'): string {
  const serviceLabel = context === 'ocpi-cpo' ? 'OCPI-CPO service' : 'Kiosk service';

  switch (statusCode) {
    case 400:
      return `Invalid request to ${serviceLabel}. Please check your configuration.`;
    case 401:
      return `Authentication failed with ${serviceLabel}. Please re-login.`;
    case 403:
      return `Access denied to ${serviceLabel}. You may not have permission to view this operator.`;
    case 404:
      return `Operator not found in ${serviceLabel}. The operator may not be configured yet.`;
    case 408:
      return `Request to ${serviceLabel} timed out. Please try again.`;
    case 429:
      return `Too many requests to ${serviceLabel}. Please wait a moment and try again.`;
    case 500:
      return `${serviceLabel} encountered an internal error. Please try again later.`;
    case 502:
      return `${serviceLabel} is temporarily unavailable. Please try again later.`;
    case 503:
      return `${serviceLabel} is currently under maintenance. Please try again later.`;
    case 504:
      return `${serviceLabel} gateway timeout. The service may be experiencing high load.`;
    default:
      if (statusCode >= 400 && statusCode < 500) {
        return `Client error when contacting ${serviceLabel} (${statusCode}).`;
      }
      if (statusCode >= 500) {
        return `Server error from ${serviceLabel} (${statusCode}).`;
      }
      return `Unexpected error from ${serviceLabel} (${statusCode}).`;
  }
}

/**
 * Map network error to user-friendly message
 */
export function getNetworkErrorMessage(context: 'ocpi-cpo' | 'kiosk'): string {
  const serviceLabel = context === 'ocpi-cpo' ? 'OCPI-CPO service' : 'Kiosk service';
  return `Network error when connecting to ${serviceLabel}. Please check your internet connection.`;
}

/**
 * Map timeout error to user-friendly message
 */
export function getTimeoutErrorMessage(context: 'ocpi-cpo' | 'kiosk'): string {
  const serviceLabel = context === 'ocpi-cpo' ? 'OCPI-CPO service' : 'Kiosk service';
  return `Connection to ${serviceLabel} timed out. The service may be experiencing delays.`;
}

/**
 * Shorten a verbose chained error message from the backend by extracting
 * just the last "Reason:" clause, which contains the actionable failure reason.
 *
 * Example input:
 *   "Failed to onboard external CPO... Reason: Failed to create subscription.
 *    Infuse EMSP operator ID:1, Reason: Registration failed: No version details."
 *
 * Example output:
 *   "Connection setup failed — Registration failed: No version details."
 *
 * If no "Reason:" pattern is found, returns the original message unchanged.
 */
export function cleanSetupErrorMessage(message: string): string {
  const lastReasonIdx = message.lastIndexOf('Reason:');
  if (lastReasonIdx !== -1) {
    const lastReason = message.substring(lastReasonIdx + 'Reason:'.length).trim();
    if (lastReason) {
      return `Connection setup failed — ${lastReason}`;
    }
  }
  return message;
}

/**
 * Get error message for failed connection check
 */
export function getConnectionCheckErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    // Check for specific error types
    if (error.message.includes('network')) {
      return 'Network connection failed. Please check your internet connection and try again.';
    }
    if (error.message.includes('timeout') || error.message.includes('timed out')) {
      return 'Connection check timed out. The services may be experiencing delays.';
    }
    if (error.message.includes('401')) {
      return 'Authentication expired. Please refresh the page and log in again.';
    }
    // Try to extract the "detail" field from a JSON response body embedded in the error message
    try {
      const bodyMatch = error.message.match(/Response Body:\s*([\s\S]+)/);
      if (bodyMatch) {
        const json = JSON.parse(bodyMatch[1].trim());
        if (json.detail) return json.detail;
        if (json.error) return json.error;
        if (json.title) return json.title;
      }
    } catch {
      // not JSON, fall through
    }
    return error.message;
  }
  return 'Failed to check connection status. Please try again.';
}

/**
 * Get error message for failed refresh operation
 */
export function getRefreshErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    if (error.message.includes('404')) {
      return 'Cannot refresh: Operator not configured in OCPI-CPO service.';
    }
    if (error.message.includes('401')) {
      return 'Authentication expired. Please refresh the page and log in again.';
    }
    return `Refresh failed: ${error.message}`;
  }
  return 'Failed to refresh OCPI data. Please try again.';
}

/**
 * Get generic error message with fallback
 */
export function getGenericErrorMessage(error: unknown, defaultMessage: string): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return defaultMessage;
}

/**
 * Format error for display (truncate long messages)
 */
export function formatErrorForDisplay(error: string, maxLength: number = 200): string {
  if (error.length <= maxLength) {
    return error;
  }
  return error.substring(0, maxLength - 3) + '...';
}

/**
 * Extract user-friendly message from API error response
 */
export function extractApiErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>;

    // Check for common API error formats
    if ('message' in errorObj && typeof errorObj.message === 'string') {
      return errorObj.message;
    }
    if ('error' in errorObj && typeof errorObj.error === 'string') {
      return errorObj.error;
    }
    if ('details' in errorObj && typeof errorObj.details === 'string') {
      return errorObj.details;
    }
    if ('statusText' in errorObj && typeof errorObj.statusText === 'string') {
      return errorObj.statusText;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}

/**
 * Determine if error is retryable
 */
export function isRetryableError(statusCode: number): boolean {
  // Retryable: 408, 429, 500, 502, 503, 504
  return [408, 429, 500, 502, 503, 504].includes(statusCode);
}

/**
 * Get retry delay based on error type
 */
export function getRetryDelay(statusCode: number): number {
  switch (statusCode) {
    case 429: // Rate limit
      return 5000; // 5 seconds
    case 503: // Service unavailable
      return 10000; // 10 seconds
    case 504: // Gateway timeout
      return 3000; // 3 seconds
    default:
      return 2000; // 2 seconds
  }
}

/**
 * Convert a raw error (Error, string or backend message) into a plain-language
 * reason a non-technical user can understand. Used in success/failure alerts.
 * Pass the HTTP status when known: the error text alone may not include it (e.g. 5xx becomes "Server error").
 */
export function getPlainLanguageFailureReason(error: unknown, status?: number | null): string {
  const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const lower = raw.toLowerCase();

  // Match a status code only as a standalone number, so ids like "connectionId=1401" don't count as 401
  const hasStatus = (...codes: number[]) =>
    (status != null && codes.includes(status)) || new RegExp(`\\b(${codes.join('|')})\\b`).test(lower);

  if (!raw && status == null) return 'An unexpected error occurred.';
  if (lower.includes('timeout') || lower.includes('timed out') || hasStatus(504, 408)) {
    return 'The charging system (CSMS) did not respond in time (timeout).';
  }
  if (lower.includes('network') || lower.includes('failed to fetch') || hasStatus(502, 503) || lower.includes('unreachable')) {
    return 'The charging system (CSMS) could not be reached.';
  }
  if (hasStatus(401, 403) || lower.includes('unauthor')) {
    return 'You are not authorized to perform this action. Please log in again.';
  }
  if (hasStatus(400) || lower.includes('validation') || lower.includes('invalid')) {
    return 'The data was rejected as invalid (validation error).';
  }
  if (hasStatus(404) || lower.includes('not found')) {
    return 'The requested location or operator was not found.';
  }

  if (!raw) return 'An unexpected error occurred.';
  const cleaned = cleanSetupErrorMessage(raw).replace(/^Connection setup failed — /, '');
  return formatErrorForDisplay(cleaned, 200);
}

/**
 * Read the HTTP status attached to an error by the API layer, if any
 */
export function getHttpStatus(error: unknown): number | null {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : null;
}

/**
 * Format a time for use in alerts, e.g. "14:32:05"
 */
export function formatAlertTime(date: Date = new Date()): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
